import { createContext, useCallback, useState } from 'react';
import foundersAPI, { Founder, FounderId } from '../api/founders';
import { Specialization } from '../api/specializations';

export interface FounderContextProps {
  founder?: Founder;
  founderSpecializations?: Specialization['id'][];
  updateFounder: (founder: Founder) => any;
  updateSpecializations: (specializations: Specialization['id'][]) => any;
  loadFounder: (founderId: FounderId) => Promise<any>;
  loadFounderSpecializations: (founderId: FounderId) => Promise<any>;
  isFounderLoading: boolean;
  isSpecializationsLoading: boolean;
  isAccessDenied: boolean;
}

export const FounderContext = createContext<FounderContextProps>({
  updateFounder: () => {},
  updateSpecializations: () => {},
  loadFounder: async () => {},
  loadFounderSpecializations: async () => {},
  isFounderLoading: true,
  isSpecializationsLoading: true,
  isAccessDenied: false,
});

interface FounderProviderProps {
  children: React.ReactNode;
}

export const FounderProvider = ({ children }: FounderProviderProps) => {
  const [founder, setFounder] = useState<Founder>();
  const [founderSpecializations, setFounderSpecializations] =
    useState<Specialization['id'][]>();
  const [isFounderLoading, setIsFounderLoading] = useState(true);
  const [isSpecializationsLoading, setIsSpecializationsLoading] =
    useState(true);
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  const updateFounder = useCallback((founder: Founder) => {
    setFounder(founder);
  }, []);

  const updateSpecializations = useCallback(
    (specializations: Specialization['id'][]) => {
      setFounderSpecializations(specializations);
    },
    [],
  );

  const loadFounder = useCallback(async (founderId: FounderId) => {
    try {
      setIsFounderLoading(true);
      const loadedFounder = await foundersAPI.getFounder(founderId);
      setFounder(loadedFounder);
      setIsFounderLoading(false);
    } catch (e: any) {
      if (e.response?.status === 403) {
        setIsAccessDenied(true);
      }
      setIsFounderLoading(false);
      console.error('error loadFounder', e);
    }
  }, []);

  const loadFounderSpecializations = useCallback(
    async (founderId: FounderId) => {
      try {
        setIsSpecializationsLoading(true);
        const specializations = await foundersAPI.getFounderSpecializations(
          founderId,
        );
        setFounderSpecializations(specializations.map((spec) => spec.id));
        setIsSpecializationsLoading(false);
      } catch (e: any) {
        setIsSpecializationsLoading(false);
        console.error('error loadFounderSpecializations', e);
      }
    },
    [],
  );

  const contextValue: FounderContextProps = {
    founder,
    isFounderLoading,
    isAccessDenied,
    loadFounder,
    updateFounder,
    founderSpecializations,
    isSpecializationsLoading,
    loadFounderSpecializations,
    updateSpecializations,
  };

  return (
    <FounderContext.Provider value={contextValue}>
      {children}
    </FounderContext.Provider>
  );
};
